"use client";

import { type ReactNode, useMemo, useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  NOTICE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  isValidAlpha,
  isValidDf,
  T_ALPHAS,
  T_DF_ROWS,
  tCritical,
} from "@/lib/tool-kit/stats-distributions/tables";

/** English strings. A translated content file overrides these through `ui`. */
export const T_TABLE_STRINGS = {
  dfLabel: "Degrees of freedom (df)",
  alphaLabel: "Significance level (α)",
  tailsLabel: "Test",
  twoTailed: "Two-tailed",
  oneTailed: "One-tailed",
  lookupHeading: "Critical value",
  resultTwo: "t = ±{t}",
  resultOne: "t = {t}",
  explainTwo:
    "With df = {df} and α = {alpha} split between both tails, reject H₀ when |t| ≥ {t}.",
  explainOne:
    "With df = {df} and α = {alpha} in one tail, reject H₀ when t ≥ {t} for an upper-tail test (t ≤ −{t} for a lower-tail test).",
  enterDf: "Enter degrees of freedom greater than 0 — decimals are fine.",
  enterAlpha: "Enter α between 0 and 1, such as 0.05.",
  caption: "Critical values of Student's t distribution",
  oneTailRow: "One tail α",
  twoTailRow: "Two tails α",
  confidenceRow: "Confidence level",
  dfHeader: "df",
  print: "Print table",
  printNote:
    "Each entry is the t with an upper-tail area of α (one tail) — equivalently 2α split across both tails. The ∞ row is the standard normal z.",
} as const;

type Strings = typeof T_TABLE_STRINGS;
type Tails = "two" | "one";

const HIGHLIGHT = "bg-primary/15 font-semibold text-foreground";
const HEADER_HIGHLIGHT = "bg-primary/10";
const TH_BASE = "px-2 py-1.5 text-right font-semibold whitespace-nowrap";
const TH_LABEL =
  "sticky left-0 bg-card px-2 py-1.5 text-left font-sans font-semibold whitespace-nowrap";

function alphaText(alpha: number, fmt: NumberFormatter): string {
  return fmt(alpha, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function dfLabel(df: number, fmt: NumberFormatter): string {
  return df === Number.POSITIVE_INFINITY ? "∞ (z)" : fmt(df);
}

/** The table column for a one-tail α, or −1 when α is not one of the printed levels. */
function columnFor(alphaOne: number): number {
  return T_ALPHAS.findIndex((alpha) => Math.abs(alpha - alphaOne) < 1e-12);
}

function TTableBlock({
  highlightRow,
  highlightColumn,
  strings,
  fmt,
}: {
  highlightRow: number;
  highlightColumn: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const values = useMemo(
    () => T_DF_ROWS.map((df) => T_ALPHAS.map((alpha) => tCritical(df, alpha))),
    []
  );
  const columnClass = (index: number) =>
    index === highlightColumn ? HEADER_HIGHLIGHT : "";
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card print:overflow-visible">
      <table className="w-full border-collapse font-mono text-sm tabular-nums">
        <caption className="px-3 pt-3 pb-1 text-left font-medium font-sans text-foreground text-sm">
          {strings.caption}
        </caption>
        <thead>
          <tr>
            <th className={TH_LABEL} scope="row">
              {strings.oneTailRow}
            </th>
            {T_ALPHAS.map((alpha, index) => (
              <th
                className={`${TH_BASE} ${columnClass(index)}`}
                key={alpha}
                scope="col"
              >
                {alphaText(alpha, fmt)}
              </th>
            ))}
          </tr>
          <tr>
            <th className={TH_LABEL} scope="row">
              {strings.twoTailRow}
            </th>
            {T_ALPHAS.map((alpha, index) => (
              <th
                className={`${TH_BASE} ${columnClass(index)}`}
                key={alpha}
                scope="col"
              >
                {alphaText(alpha * 2, fmt)}
              </th>
            ))}
          </tr>
          <tr className="border-border border-b">
            <th className={TH_LABEL} scope="row">
              {strings.confidenceRow}
            </th>
            {T_ALPHAS.map((alpha, index) => (
              <th
                className={`${TH_BASE} font-normal text-muted-foreground ${columnClass(index)}`}
                key={alpha}
                scope="col"
              >
                {fmt((1 - 2 * alpha) * 100, { maximumFractionDigits: 1 })}%
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {T_DF_ROWS.map((df, row) => (
            <tr className="border-border/60 border-b last:border-0" key={df}>
              <th
                className={`sticky left-0 whitespace-nowrap bg-card px-2 py-1 text-left font-semibold ${row === highlightRow ? HEADER_HIGHLIGHT : ""}`}
                scope="row"
              >
                {dfLabel(df, fmt)}
              </th>
              {T_ALPHAS.map((alpha, column) => (
                <td
                  className={`whitespace-nowrap px-2 py-1 text-right text-muted-foreground ${row === highlightRow && column === highlightColumn ? HIGHLIGHT : ""}`}
                  key={alpha}
                >
                  {fmt(values[row][column], {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Lookup({
  df,
  alpha,
  tails,
  strings,
  fmt,
}: {
  df: number | null;
  alpha: number | null;
  tails: Tails;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  let body: ReactNode;
  if (df === null || !isValidDf(df)) {
    body = <p className="text-muted-foreground">{strings.enterDf}</p>;
  } else if (alpha === null || !isValidAlpha(alpha)) {
    body = <p className="text-muted-foreground">{strings.enterAlpha}</p>;
  } else {
    const critical = tCritical(df, tails === "two" ? alpha / 2 : alpha);
    const shown = fmt(critical, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
    const values = {
      t: shown,
      df: fmt(df, { maximumFractionDigits: 4 }),
      alpha: fmt(alpha, { maximumFractionDigits: 6 }),
    };
    body = (
      <>
        <p className="font-mono font-semibold text-2xl text-foreground">
          {t(tails === "two" ? strings.resultTwo : strings.resultOne, values)}
        </p>
        <p className="text-muted-foreground">
          {t(tails === "two" ? strings.explainTwo : strings.explainOne, values)}
        </p>
      </>
    );
  }
  return (
    <div
      aria-live="polite"
      className={`${CARD} space-y-2 text-sm print:hidden`}
    >
      <h2 className={WIDGET_H2}>{strings.lookupHeading}</h2>
      {body}
    </div>
  );
}

export default function TTableWidget() {
  const strings = useToolUi(T_TABLE_STRINGS);
  const fmt = useToolNumber();
  const [dfText, setDfText] = useState("10");
  const [alphaInput, setAlphaInput] = useState("0.05");
  const [tails, setTails] = useState<Tails>("two");

  const df = parseNumberInput(dfText);
  const alpha = parseNumberInput(alphaInput);
  const perTail = tails === "two" ? 2 : 1;
  const highlightRow = df === null ? -1 : T_DF_ROWS.indexOf(df);
  const highlightColumn = alpha === null ? -1 : columnFor(alpha / perTail);

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-3 print:hidden">
        <TextField
          id="tt-df"
          label={strings.dfLabel}
          numeric
          onChange={setDfText}
          value={dfText}
        />
        <TextField
          id="tt-alpha"
          label={strings.alphaLabel}
          numeric
          onChange={setAlphaInput}
          value={alphaInput}
        />
        <SelectField
          id="tt-tails"
          label={strings.tailsLabel}
          onChange={setTails}
          options={[
            { value: "two", label: strings.twoTailed },
            { value: "one", label: strings.oneTailed },
          ]}
          value={tails}
        />
      </section>
      <Lookup alpha={alpha} df={df} fmt={fmt} strings={strings} tails={tails} />
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className={`${NOTICE} flex-1`}>{strings.printNote}</p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        </div>
        <TTableBlock
          fmt={fmt}
          highlightColumn={highlightColumn}
          highlightRow={highlightRow}
          strings={strings}
        />
      </section>
    </>
  );
}
